<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import IaAgentModal from './IaAgentModal.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedAgent = ref({});

const records = computed(() => getters['iaAgents/getIAAgents'].value);
const uiFlags = computed(() => getters['iaAgents/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedAgent.value.name}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  selectedAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeleteConfirmationPopup.value = true;
  selectedAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('iaAgents/delete', id);
    useAlert(t('IA_AGENTS_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('IA_AGENTS_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedAgent.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(selectedAgent.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('IA_AGENTS_MGMT.LIST.TABLE_HEADER.NAME'),
    t('IA_AGENTS_MGMT.LIST.TABLE_HEADER.USER'),
    t('IA_AGENTS_MGMT.LIST.TABLE_HEADER.WEBHOOK'),
    t('IA_AGENTS_MGMT.LIST.TABLE_HEADER.INBOXES'),
    t('IA_AGENTS_MGMT.LIST.TABLE_HEADER.STATUS'),
  ];
});

// GK-HUB: contagem de inboxes dedicadas pra badge
const dedicatedCount = agent => {
  const list = agent?.inbox_assignments || [];
  return list.filter(a => a.is_dedicated).length;
};

onBeforeMount(() => {
  store.dispatch('iaAgents/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('IA_AGENTS_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('IA_AGENTS_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('IA_AGENTS_MGMT.HEADER')"
        :description="$t('IA_AGENTS_MGMT.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('IA_AGENTS_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="agent in records" :key="agent.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-col">
                <span class="mb-0.5 font-medium break-words text-n-slate-12">
                  {{ agent.name }}
                </span>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-if="agent.is_default_n1"
                    class="inline-flex items-center w-fit px-1.5 py-0.5 rounded text-[10px] font-semibold bg-n-brand text-white"
                  >
                    {{ $t('IA_AGENTS_MGMT.BADGE_N1') }}
                  </span>
                  <span
                    v-if="dedicatedCount(agent) > 0"
                    class="inline-flex items-center whitespace-nowrap w-fit px-1.5 py-0.5 rounded text-[10px] font-medium bg-n-solid-3 text-n-brand border border-n-brand/30"
                  >
                    {{
                      $t('IA_AGENTS_MGMT.BADGE_DEDICATED', {
                        count: dedicatedCount(agent),
                      })
                    }}
                  </span>
                </div>
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ agent.user_name || '-' }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="inline-block max-w-[200px] truncate text-xs text-n-slate-11"
                :title="agent.webhook_url"
              >
                {{ agent.webhook_url }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div v-if="agent.inboxes?.length" class="flex flex-wrap gap-1">
                <span
                  v-for="inbox in agent.inboxes"
                  :key="inbox.id"
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-n-solid-3 text-n-slate-12"
                >
                  {{ inbox.name }}
                </span>
              </div>
              <span v-else class="text-xs text-n-slate-11">
                {{ $t('IA_AGENTS_MGMT.LIST.ALL_INBOXES') }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  agent.active
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{
                  agent.active
                    ? $t('IA_AGENTS_MGMT.STATUS.ACTIVE')
                    : $t('IA_AGENTS_MGMT.STATUS.INACTIVE')
                }}
              </span>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('IA_AGENTS_MGMT.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[agent.id]"
                  @click="openEditPopup(agent)"
                />
                <Button
                  v-tooltip.top="$t('IA_AGENTS_MGMT.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[agent.id]"
                  @click="openDeletePopup(agent)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <IaAgentModal @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <IaAgentModal :agent="selectedAgent" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('IA_AGENTS_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('IA_AGENTS_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('IA_AGENTS_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('IA_AGENTS_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
